import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { Loader2, Lock, Mail, Smartphone, User } from "lucide-react";
import { motion } from "framer-motion";

const LoginPage = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [identifierType, setIdentifierType] = useState<"email" | "phone" | "username">("email");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithFirebase } = useAuth();

  // Auto-detect identifier type
  useEffect(() => {
    if (identifier.includes("@")) {
      setIdentifierType("email");
    } else if (/^\d+$/.test(identifier)) {
      setIdentifierType("phone");
    } else if (identifier.trim().length > 0) {
      setIdentifierType("username");
    }
  }, [identifier]);

  useEffect(() => {
    document.getElementById("identifier")?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!identifier || !password) {
      return setError(t("login.error.empty"));
    }

    // Enhanced validation based on identifier type
    if (identifierType === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
      return setError(t("login.error.email"));
    }

    if (identifierType === "phone" && (identifier.length < 10 || identifier.length > 15)) {
      return setError(t("login.error.phone"));
    }

    if (password.length < 6) {
      return setError(t("login.error.password"));
    }

    setSubmitting(true);

    try {
      const success = await login(identifier, password, () => {
        navigate(location.state?.from?.pathname || "/", { replace: true });
      });

      if (!success) setError(t("login.error.credentials"));
    } catch {
      setError(t("login.error.general"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFirebaseLogin = async () => {
    setGoogleLoading(true);
    setError("");

    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account"
      });
      
      await signInWithPopup(auth, provider);
      await loginWithFirebase(() => {
        navigate(location.state?.from?.pathname || "/", { replace: true });
      });
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Google login failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  // Get appropriate icon for identifier type
  const getIdentifierIcon = () => {
    switch (identifierType) {
      case "email":
        return <Mail className="w-5 h-5 text-gray-400" />;
      case "phone":
        return <Smartphone className="w-5 h-5 text-gray-400" />;
      case "username":
        return <User className="w-5 h-5 text-gray-400" />;
      default:
        return <Mail className="w-5 h-5 text-gray-400" />;
    }
  };

  // Get placeholder based on identifier type
  const getPlaceholder = () => {
    switch (identifierType) {
      case "email":
        return "example@email.com";
      case "phone":
        return "+201234567890";
      case "username":
        return "john_doe";
      default:
        return t("login.identifier.placeholder");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 to-purple-100/50 -z-10" />
        
        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm w-full p-8 rounded-3xl shadow-2xl space-y-8 border border-white/20">
          {/* Logo/Brand Area */}
          <div className="text-center space-y-3">
            <div className="flex justify-center mb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Lock className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="space-y-1">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t("login.title")}
              </h1>
              <p className="text-gray-500 text-sm">{t("login.subtitle")}</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 bg-red-50/80 border border-red-200 rounded-xl text-red-600 text-sm backdrop-blur-sm"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-red-500 text-xs">!</span>
                </div>
                <span>{error}</span>
              </div>
            </motion.div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Identifier Input */}
            <div className="space-y-2">
              <label htmlFor="identifier" className="block text-sm font-semibold text-gray-700">
                {t("login.identifier.label")}
                <span className="text-xs font-normal text-gray-500 ml-1">
                  ({identifierType === "email" ? "Email" : identifierType === "phone" ? "Phone" : "Username"})
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {getIdentifierIcon()}
                </div>
                <input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value.trim())}
                  disabled={submitting}
                  className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                  placeholder={getPlaceholder()}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                {t("login.password.label")}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  className="w-full pl-11 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={submitting}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={submitting}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t("login.submitting")}</span>
                </>
              ) : (
                <>
                  <span>{t("login.submit")}</span>
                  <motion.div
                    initial={{ x: 0 }}
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </motion.div>
                </>
              )}
            </motion.button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">{t("login.or") || "OR"}</span>
            </div>
          </div>

          {/* Google Login */}
          <motion.button
            onClick={handleFirebaseLogin}
            disabled={googleLoading || submitting}
            whileTap={{ scale: 0.98 }}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 py-3.5 rounded-xl font-medium shadow-sm hover:shadow-md hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            {googleLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
                <span className="text-gray-700">Connecting to Google...</span>
              </>
            ) : (
              <>
                <div className="w-5 h-5">
                  <img src="https://www.svgrepo.com/show/355037/google.svg" alt="Google" className="w-full h-full" />
                </div>
                <span className="text-gray-700 group-hover:text-blue-600 transition-colors">
                  {t("login.google")}
                </span>
              </>
            )}
          </motion.button>

          {/* Links Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Link
              to="/forgot-password"
              className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 transition-colors p-3 rounded-lg hover:bg-blue-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span>{t("login.forgotPassword")}</span>
            </Link>
            <Link
              to="/register"
              className="flex items-center justify-center gap-2 text-purple-600 hover:text-purple-700 transition-colors p-3 rounded-lg hover:bg-purple-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <span>{t("login.register")}</span>
            </Link>
          </div>

          {/* Additional Info */}
          <div className="text-center text-xs text-gray-400 pt-4 border-t border-gray-100">
            <p>By continuing, you agree to our Terms & Privacy Policy</p>
          </div>
        </div>

        {/* Loading Overlay */}
        {(submitting || googleLoading) && (
          <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-blue-100 rounded-full"></div>
                <div className="absolute top-0 left-0 w-12 h-12 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <p className="text-gray-700 font-medium">
                {submitting ? "Signing you in..." : "Connecting to Google..."}
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default LoginPage;